import { useState, useEffect } from 'react';
import api from '../services/api';

// In-memory cache for teams data
let teamsCache = null;
let teamsCachePromise = null;

/**
 * Fetch teams from API and cache the result
 * @returns {Promise<Array>} Array of team objects
 */
export const fetchTeams = async () => {
  // Return cached data if available
  if (teamsCache) {
    return teamsCache;
  }

  // Return existing promise if fetch is in progress
  if (teamsCachePromise) {
    return teamsCachePromise;
  }

  // Start new fetch
  teamsCachePromise = api
    .get('/teams')
    .then((response) => {
      teamsCache = response.data.teams;
      teamsCachePromise = null;
      return teamsCache;
    })
    .catch((error) => {
      console.error('Failed to fetch teams:', error);
      teamsCachePromise = null;
      throw error;
    });

  return teamsCachePromise;
};

/**
 * Get logo path for a team by ID
 * @param {number} teamId - The team ID
 * @returns {string} Path to the team logo
 */
export const getTeamLogoPath = (teamId) => {
  if (!teamsCache) {
    return '/assets/team-logos/default.png';
  }

  const team = teamsCache.find((t) => t.id === teamId);
  if (!team || !team.logo_filename) {
    return '/assets/team-logos/default.png';
  }

  return `/assets/team-logos/${team.logo_filename}`;
};

/**
 * Get team data by ID
 * @param {number} teamId - The team ID
 * @returns {object|null} Team object or null if not found
 */
export const getTeamById = (teamId) => {
  if (!teamsCache) {
    return null;
  }

  return teamsCache.find((t) => t.id === teamId) || null;
};

/**
 * React hook to load and use teams data
 * @returns {object} { teams, loading, error, getTeamLogoPath, getTeamById }
 */
export const useTeams = () => {
  const [teams, setTeams] = useState(teamsCache || []);
  const [loading, setLoading] = useState(!teamsCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If cache is already populated, use it
    if (teamsCache) {
      setTeams(teamsCache);
      setLoading(false);
      return;
    }

    // Otherwise fetch from API
    fetchTeams()
      .then((data) => {
        setTeams(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, []);

  return {
    teams,
    loading,
    error,
    getTeamLogoPath,
    getTeamById,
  };
};

/**
 * Clear the teams cache (useful for testing or forcing refresh)
 */
export const clearTeamsCache = () => {
  teamsCache = null;
  teamsCachePromise = null;
};
